import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import SquareLoader from './SquareLoader';
import { useGroupContext } from '../lib/groups/context';

type Props = {
    state: 'missing' | 'not-member' | 'unknown';
    slug: string;
    onCreate?: () => Promise<void> | void;
    onRefresh?: () => Promise<void> | void;
};

export default function VaultGate({ state, slug, onCreate, onRefresh }: Props) {
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [typedQuery, setTypedQuery] = useState<string>('');
    const { reload } = useGroupContext();

    const handleCreate = async () => {
        if (!onCreate) return;
        try {
            setError(null);
            setCreating(true);
            await onCreate();
        } catch (e: any) {
            console.error(e);
            setError(e?.message || 'Failed to create vault');
        } finally {
            setCreating(false);
        }
    };

    // Try to recover the user's original query; fall back to slug and store for Home restoration
    useEffect(() => {
        try {
            const last = sessionStorage.getItem('gv:lastFindQuery');
            if (last && last.trim()) {
                setTypedQuery(last.trim());
            } else if (slug) {
                setTypedQuery(slug);
                sessionStorage.setItem('gv:lastFindQuery', slug);
            }
        } catch {
            setTypedQuery(slug);
        }
    }, [slug]);

    const goHome = (focusInvites?: boolean) => {
        try {
            if (focusInvites) sessionStorage.setItem('gv:focusInvites', '1');
        } catch { /* ignore */ }
        try {
            window.history.pushState({}, '', '/');
            reload();
        } catch {
            window.location.href = '/';
        }
    };

    return (
        <div className="min-h-screen bg-black flex items-center justify-center px-6">
            {/* Top-corner back arrow (professional style) */}
            <button
                onClick={() => goHome(false)}
                aria-label="Back"
                className="absolute top-4 right-4 inline-flex items-center justify-center h-9 w-9 rounded-full bg-gray-900/70 border border-gray-800/70 text-gray-300 hover:text-white hover:bg-gray-800/70 shadow-sm backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-gray-700 transition"
                title="Back"
            >
                <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="max-w-lg w-full text-center text-white">
                <img
                    src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                    alt="Vault Contact Logo"
                    className="h-14 w-14 mx-auto mb-6 object-contain"
                />
                {state === 'unknown' && (
                    <>
                        <SquareLoader size="sm" className="mb-4 mx-auto" />
                        <p className="text-gray-400">Checking vault access…</p>
                    </>
                )}

                {(state === 'missing' || state === 'not-member') && (
                    <>
                        <h1 className="text-2xl font-light tracking-wide mb-2">Access Denied</h1>
                        <p className="text-gray-400 mb-6 break-words">
                            Sorry, you don't have access to the <span className="text-white">{typedQuery || slug}</span> vault.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            {onRefresh && (
                                <button onClick={() => goHome(true)} className="px-5 py-3 font-semibold bg-white text-black hover:bg-gray-100">
                                    I HAVE AN INVITE
                                </button>
                            )}
                            <button
                                onClick={() => goHome(false)}
                                className="px-5 py-3 font-semibold bg-gray-900 text-gray-300 hover:bg-gray-800 border border-gray-700"
                            >
                                BACK
                            </button>
                        </div>
                        {error && <p className="text-red-400 mt-4 text-sm">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
}
